import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../lib/seo';
import { ContactForm } from '../components/ContactForm';

export function About({ settings }) {
  const phone = settings?.phone || "82 82 10 10";
  const hours = settings?.hours || "Mandag - Lørdag: 9 - 18";
  const coverage = settings?.coverage_area || "Hele Sjælland – minus øerne";

  return (
    <>
      <SEO 
        title="Om Fodtid | Klaus Just & Professionel Fodpleje"
        description="Læs om Fodtid og Klaus Just. Med over 15 års erfaring og mere end 8.000 tilfredse kunder tilbyder vi tryg og professionel mobil fodpleje på hele Sjælland (minus øerne)."
      />

      {/* Header */}
      <section className="bg-canvas py-14 sm:py-18 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center space-y-3">
          <span className="text-xs uppercase font-bold tracking-widest text-brand-charcoal bg-surface px-3.5 py-1 rounded-full border border-brand-border">
            Faglighed • Erfaring • Nærvær
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-brand-charcoal">
            Om Fodtid & Klaus Just
          </h1>
          <p className="text-brand-muted text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Altid sunde fødder – Velvære til dine fødder gør en verden til forskel for hele kroppen.
          </p>
        </div>
      </section>

      {/* Story & Philosophy Section */}
      <section className="py-16 sm:py-24 bg-canvas border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
            
            <div className="lg:col-span-6 space-y-6">
              <div className="space-y-2">
                <span className="text-xs uppercase font-bold tracking-widest text-brand-muted">
                  Må jeg hjælpe dig?
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-charcoal leading-tight">
                  Over 15 års erfaring med fødders sundhed
                </h2>
              </div>

              <p className="text-brand-muted text-sm sm:text-base leading-relaxed">
                Mit navn er <strong>Klaus Just</strong>, og jeg driver Fodtid (tidligere kendt som Den Glade Fodmand). Med over 15 års erfaring inden for fodpleje og zoneterapi bruger jeg de nyeste teknikker og 100% naturlige plejeprodukter for at sikre dig de bedste resultater.
              </p>

              <p className="text-brand-muted text-sm sm:text-base leading-relaxed">
                Uanset om du har prøvet andre behandlere før, eller om det er første gang, du overvejer en fodbehandling, er det vigtigt, at du føler dig taget seriøst. En fodbehandling er ikke bare en luksus – det er en klinisk behandling, der kan hjælpe med at løse og forebygge en lang række problemer.
              </p>

              <div className="p-6 bg-surface/70 rounded-2xl border border-brand-border space-y-2">
                <div className="font-serif font-bold text-brand-charcoal text-base">
                  "Jeg har hjulpet over 8.000 kunder til sunde fødder"
                </div>
                <p className="text-xs text-brand-muted leading-relaxed">
                  Fodproblemer kan vise sig på mange måder – føleforstyrrelser, ligtorne, hård hud eller hælsporer, for bare at nævne nogle få. Fødderne er den del af kroppen, der bliver mest belastet. Alligevel er de ofte den del, vi glemmer at passe på, indtil smerterne melder sig.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative">
                <div className="aspect-4/3 rounded-3xl overflow-hidden shadow-md border border-[#E7E0D2]">
                  <img
                    src="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/samssa-bil-logo-2.png"
                    alt="Klaus Just ankommer med mobil servicebil til hjemmebehandling"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="absolute -bottom-6 -left-6 bg-[#1C1917] text-white p-5 rounded-2xl shadow-lg border border-[#E7E0D2] hidden sm:block max-w-xs">
                  <div className="font-bold text-[#A67C3D] text-lg">8.000+ fødder</div>
                  <div className="text-xs text-stone-300">har været i trygge hænder hos Klaus Just siden 2009.</div>
                </div>
              </div>
            </div>

          </div>

          {/* Section: Dine fødder – et sandt mesterværk */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-12 border-t border-brand-border">
            
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="aspect-4/3 rounded-3xl overflow-hidden shadow-md border border-[#E7E0D2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/fodtid-cozy-home-treatment-livingroom.png"
                  alt="Tryg og skånsom fodbehandling i eget hjem"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-6 space-y-6 order-1 lg:order-2">
              <span className="text-xs uppercase font-bold tracking-widest text-brand-muted">
                Kroppens fundament
              </span>
              
              {/* Mixed Heading Group */}
              <div className="space-y-1">
                <h2 className="font-serif text-3xl sm:text-4xl font-medium tracking-wide uppercase text-brand-charcoal leading-tight">
                  Dine Fødder –
                </h2>
                <span className="block font-cursive text-4xl sm:text-5xl text-brand-charcoal -mt-2 font-normal">
                  et sandt mesterværk
                </span>
              </div>

              <p className="text-brand-muted text-sm sm:text-base leading-relaxed">
                Vidste du, at fødderne indeholder en fjerdedel af kroppens knogler og tusindvis af nerveender? De bærer os gennem hele livet, ofte uden at vi skænker dem megen opmærksomhed.
              </p>

              <div className="space-y-3 text-xs text-brand-charcoal">
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                  <span><strong>Skånsom og smertefri behandling</strong> med sterilt specialudstyr.</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                  <span><strong>100% naturlige urtecremer</strong> tilpasset din huds tilstand.</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                  <span><strong>Behandling i eget hjem</strong> uden stress, trapper eller transport.</span>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-brand-charcoal hover:bg-black text-canvas font-bold px-7 py-3.5 rounded-full text-xs uppercase tracking-wider shadow transition-colors"
                >
                  <i className="fa-regular fa-calendar-check text-brand-accent" aria-hidden="true" />
                  <span>Book et hjemmebesøg</span>
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 sm:py-24 bg-surface/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="bg-canvas rounded-3xl p-6 sm:p-10 border border-brand-border shadow-sm">
            <div className="text-center mb-8 space-y-2">
              <span className="text-xs uppercase font-bold tracking-widest text-brand-muted">
                Kontakt Klaus Just
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-brand-charcoal">
                Bestil din fodbehandling i dag
              </h2>
              <p className="text-brand-muted text-xs sm:text-sm">
                Skriv til os, eller ring direkte på <a href={`tel:${phone.replace(/\s+/g, '')}`} className="font-bold text-brand-charcoal underline">{phone}</a> (mandag-lørdag 9-18).
              </p>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}
